<template>
  <div class="stakeholder-page">
    <section class="stakeholder-shell">
      <div class="stakeholder-hero">
        <div class="hero-copy">
          <nav class="stakeholder-breadcrumb" aria-label="Breadcrumb">
            <span>Home</span>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">Stakeholder Inventory</span>
          </nav>

          <p class="hero-kicker">Partnership Directory</p>
          <h1>Navigate stakeholder portfolios with a clearer, more premium overview</h1>
          <p class="hero-description">
            Explore strategic collaboration areas, move quickly into each stakeholder cluster, and
            review the ecosystem through a more polished administrative entry point.
          </p>
          <div class="hero-actions">
            <span class="hero-chip">Strategic partners</span>
            <span class="hero-chip">Domain-based access</span>
            <span class="hero-chip">Portfolio discovery</span>
          </div>
        </div>

        <div class="hero-metrics">
          <article class="metric-card">
            <span class="metric-label">Focus clusters</span>
            <strong class="metric-value">{{ stakeholderCategories.length }}</strong>
            <span class="metric-meta">Specialized areas of stakeholder work</span>
          </article>
          <article class="metric-card">
            <span class="metric-label">Experience</span>
            <strong class="metric-value">Curated</strong>
            <span class="metric-meta">Designed for faster portfolio navigation</span>
          </article>
          <article class="metric-card metric-card--highlight">
            <span class="metric-label">Priority Route</span>
            <strong class="metric-value">Research</strong>
            <span class="metric-meta">Most active collaboration entry point</span>
          </article>
        </div>
      </div>

      <section class="catalog-section">
        <div class="section-header">
          <div>
            <p class="section-kicker">Inventory Categories</p>
            <h2>Stakeholder domains</h2>
          </div>
          <p class="section-note">
            Select a domain to review the stakeholders operating within that focus area.
          </p>
        </div>

        <div class="spotlight-banner">
          <div>
            <p class="spotlight-kicker">Portfolio Spotlight</p>
            <h3>Five curated pathways into the stakeholder ecosystem</h3>
          </div>
          <p>
            Each card below opens a domain-specific directory so teams can review actors,
            relationships, and collaboration roles with less friction.
          </p>
        </div>

        <div class="stakeholder-grid">
          <router-link
            v-for="category in stakeholderCategories"
            :key="category.title"
            :to="category.to"
            class="stakeholder-card"
            :class="category.accentClass"
          >
            <div class="card-orb"></div>
            <div class="card-icon" aria-hidden="true">{{ category.icon }}</div>
            <span class="card-index">{{ category.index }}</span>
            <h3>{{ category.title }}</h3>
            <p>{{ category.description }}</p>
            <div class="card-footer">
              <span>{{ category.tag }}</span>
              <strong>Open directory</strong>
            </div>
          </router-link>
        </div>
      </section>
    </section>
  </div>
</template>

<script>
export default {
  data() {
    return {
      stakeholderCategories: [
        {
          index: '01',
          icon: 'RK',
          title: 'Research and Knowledge',
          description:
            'Review stakeholders advancing evidence generation, analytics, publications, and technical knowledge support.',
          tag: 'Evidence and insight',
          to: '/admin/research',
          accentClass: 'stakeholder-card--sage',
        },
        {
          index: '02',
          icon: 'ME',
          title: 'Management and Evaluation',
          description:
            'Access organizations focused on program monitoring, evaluation systems, and performance management.',
          tag: 'Performance oversight',
          to: '/admin/management-evaluation',
          accentClass: 'stakeholder-card--sand',
        },
        {
          index: '03',
          icon: 'HS',
          title: 'Health System Strengthening',
          description:
            'Explore partners contributing to service delivery resilience, institutional capacity, and system reform.',
          tag: 'Systems support',
          to: '/admin/health-system',
          accentClass: 'stakeholder-card--moss',
        },
        {
          index: '04',
          icon: 'PP',
          title: 'Policy and Planning',
          description:
            'Open the policy-focused cluster for planning support, strategy alignment, and governance contributions.',
          tag: 'Strategy and planning',
          to: '/admin/policy-planning',
          accentClass: 'stakeholder-card--copper',
        },
        {
          index: '05',
          icon: 'IC',
          title: 'International Cooperation',
          description:
            'Navigate cross-border and development cooperation stakeholders coordinating joint interventions and support.',
          tag: 'Partnerships and diplomacy',
          to: '/admin/international-coperation',
          accentClass: 'stakeholder-card--ink',
        },
      ],
    };
  },
};
</script>

<style scoped>
.stakeholder-page {
  min-height: 100vh;
  padding: 1.5rem 1rem 2rem;
  background: radial-gradient(circle at top left, rgba(233, 222, 205, 0.75), transparent 26%),
    radial-gradient(circle at top right, rgba(119, 57, 22, 0.1), transparent 22%),
    linear-gradient(180deg, #f7f1e8 0%, #f7f6f2 45%, #f1f3ef 100%);
  min-width: 0;
  overflow-x: hidden;
}

.stakeholder-shell {
  max-width: 1360px;
  margin: 0 auto;
  min-width: 0;
}

.stakeholder-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(280px, 0.9fr);
  gap: 1.5rem;
  padding: 1.8rem;
  border: 1px solid rgba(70, 38, 19, 0.08);
  border-radius: 30px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(251, 248, 243, 0.92)), #fff;
  box-shadow: 0 24px 56px rgba(53, 38, 24, 0.1);
  min-width: 0;
}

.hero-copy,
.hero-metrics,
.catalog-section,
.stakeholder-card {
  min-width: 0;
}

.stakeholder-breadcrumb {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #766b60;
  font-size: 0.92rem;
}

.breadcrumb-separator {
  opacity: 0.55;
}

.breadcrumb-current {
  color: #51301c;
  font-weight: 600;
}

.hero-kicker,
.section-kicker {
  margin-bottom: 0.65rem;
  color: #9a592f;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-copy h1 {
  max-width: 15ch;
  margin-bottom: 0.85rem;
  color: #2f1c11;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(2.1rem, 4vw, 3.5rem);
  font-weight: 700;
  line-height: 1.03;
  white-space: normal;
  overflow-wrap: anywhere;
}

.hero-description {
  max-width: 62ch;
  margin-bottom: 0;
  color: #62574e;
  font-size: 1rem;
  line-height: 1.75;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
  margin-top: 1.25rem;
}

.hero-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.65rem 0.9rem;
  border: 1px solid rgba(85, 53, 34, 0.08);
  border-radius: 999px;
  background: rgba(249, 244, 237, 0.8);
  color: #6f5848;
  font-size: 0.88rem;
  font-weight: 600;
}

.hero-metrics {
  display: grid;
  gap: 1rem;
}

.metric-card {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 1.15rem 1.25rem;
  border: 1px solid rgba(70, 38, 19, 0.08);
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(249, 244, 237, 0.9));
  min-width: 0;
}

.metric-card--highlight {
  background: linear-gradient(145deg, rgba(76, 49, 33, 0.96), rgba(126, 78, 47, 0.92));
  box-shadow: 0 18px 28px rgba(82, 51, 31, 0.16);
}

.metric-card--highlight .metric-label,
.metric-card--highlight .metric-meta {
  color: rgba(255, 244, 232, 0.82);
}

.metric-card--highlight .metric-value {
  color: #fff8f1;
}

.metric-label {
  color: #75675b;
  font-size: 0.88rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.metric-value {
  color: #2f1c11;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 2rem;
  line-height: 1.1;
}

.metric-meta {
  color: #8b7f74;
  font-size: 0.93rem;
}

.catalog-section {
  margin-top: 1.5rem;
  padding: 1.35rem;
  border: 1px solid rgba(70, 38, 19, 0.08);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(16px);
}

.section-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.2rem;
}

.section-header h2 {
  margin-bottom: 0;
  color: #2f1c11;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(1.5rem, 2.8vw, 2.1rem);
  font-weight: 700;
}

.section-note {
  max-width: 34ch;
  margin-bottom: 0;
  color: #70655b;
  line-height: 1.7;
}

.spotlight-banner {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.15rem;
  padding: 1.2rem 1.25rem;
  border: 1px solid rgba(70, 38, 19, 0.08);
  border-radius: 24px;
  background: radial-gradient(circle at top right, rgba(170, 113, 74, 0.12), transparent 30%),
    linear-gradient(135deg, rgba(250, 245, 239, 0.98), rgba(255, 255, 255, 0.92));
}

.spotlight-kicker {
  margin-bottom: 0.45rem;
  color: #a35f34;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.spotlight-banner h3 {
  margin-bottom: 0;
  color: #372115;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 1.45rem;
  font-weight: 700;
}

.spotlight-banner p {
  max-width: 38ch;
  margin-bottom: 0;
  color: #6f6259;
  line-height: 1.7;
}

.stakeholder-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 1rem;
}

.stakeholder-card {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 290px;
  padding: 1.35rem;
  border: 1px solid rgba(70, 38, 19, 0.08);
  border-radius: 24px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(248, 243, 236, 0.92)), #fff;
  box-shadow: 0 20px 36px rgba(58, 40, 23, 0.08);
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.stakeholder-card:hover {
  transform: translateY(-4px);
  border-color: rgba(154, 89, 47, 0.22);
  box-shadow: 0 28px 46px rgba(58, 40, 23, 0.12);
}

.stakeholder-card--sage {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(243, 246, 240, 0.94)), #fff;
}

.stakeholder-card--sand {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(248, 242, 232, 0.94)), #fff;
}

.stakeholder-card--moss {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(238, 244, 236, 0.94)), #fff;
}

.stakeholder-card--copper {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(248, 239, 232, 0.94)), #fff;
}

.stakeholder-card--ink {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(239, 242, 246, 0.94)), #fff;
}

.card-orb {
  position: absolute;
  top: -78px;
  right: -30px;
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(186, 118, 67, 0.14), transparent 68%);
  pointer-events: none;
}

.card-index,
.card-icon,
.stakeholder-card h3,
.stakeholder-card p,
.card-footer {
  position: relative;
  z-index: 1;
}

.card-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  margin-bottom: 1rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.72);
  color: #8f542d;
  font-size: 0.86rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  box-shadow: inset 0 0 0 1px rgba(143, 84, 45, 0.08);
}

.card-index {
  display: inline-block;
  margin-bottom: 0.85rem;
  color: #9b8d7f;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.stakeholder-card h3 {
  margin-bottom: 0.9rem;
  color: #3a2417;
  font-size: 1.18rem;
  font-weight: 700;
  line-height: 1.4;
}

.stakeholder-card p {
  margin-bottom: 0;
  color: #6b5f55;
  line-height: 1.7;
}

.card-footer {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: auto;
  padding-top: 1.2rem;
}

.card-footer span {
  color: #9a592f;
  font-size: 0.84rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.card-footer strong {
  color: #3b2517;
  font-size: 0.95rem;
}

@media (max-width: 1280px) {
  .stakeholder-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 991px) {
  .stakeholder-page {
    padding-top: 0;
  }

  .stakeholder-hero,
  .section-header,
  .spotlight-banner {
    align-items: stretch;
  }

  .stakeholder-hero {
    grid-template-columns: 1fr;
  }

  .stakeholder-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .stakeholder-page {
    padding: 0;
  }

  .stakeholder-hero,
  .catalog-section,
  .stakeholder-card {
    border-radius: 22px;
  }

  .stakeholder-hero,
  .catalog-section {
    padding: 1.15rem;
  }

  .stakeholder-grid {
    grid-template-columns: 1fr;
  }

  .stakeholder-card {
    min-height: 0;
  }

  .hero-copy h1 {
    max-width: none;
    font-size: clamp(1.9rem, 8vw, 2.7rem);
    line-height: 1.08;
  }

  .hero-description,
  .section-note,
  .spotlight-banner p {
    max-width: none;
  }
}

@media (max-width: 600px) {
  .stakeholder-page {
    padding-top: 0;
  }
}
</style>
